import type { GateResult } from "@iamu/shared";
import { AlertTriangle, Check, Minus, X } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CONDITION_LABELS } from "./gate-vocabulary";

type Condition = GateResult["conditions"][number];

function Outcome({ condition }: { condition: Condition }) {
  if (condition.level === "off") {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Minus className="size-4" aria-hidden="true" />
        Not enforced
      </span>
    );
  }
  if (condition.outcome === "ok") {
    return (
      <span className="inline-flex items-center gap-1.5 text-finding-helps">
        <Check className="size-4" aria-hidden="true" />
        Pass
      </span>
    );
  }
  return condition.outcome === "error" ? (
    <span className="inline-flex items-center gap-1.5 font-medium text-severity-critical">
      <X className="size-4" aria-hidden="true" />
      Fail
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 font-medium text-severity-important">
      <AlertTriangle className="size-4" aria-hidden="true" />
      Warning
    </span>
  );
}

function scopeNote(filter: Record<string, unknown>): string | null {
  if (typeof filter.capability === "string") return `in ${filter.capability}`;
  if (filter.criticality === "critical_path") return "critical-path scenarios";
  return null;
}

/**
 * The gate's conditions with their outcome, actual value and the findings
 * behind a failing count (evidence links).
 */
export function GateConditionsTable({
  result,
  projectId,
}: {
  result: GateResult;
  projectId: string;
}) {
  const titleOf = new Map(result.items.map((i) => [i.canonical_id, i.title]));
  const evidence = (ids: string[]) =>
    ids.length === 0 ? null : (
      <ul className="space-y-1">
        {ids.slice(0, 3).map((id) => (
          <li key={id} className="max-w-56 truncate md:max-w-72">
            <Link
              to={`/projects/${projectId}/findings/${id}`}
              className="text-foreground underline underline-offset-4"
            >
              {titleOf.get(id) ?? "View finding"}
            </Link>
          </li>
        ))}
        {ids.length > 3 && (
          <li className="text-sm text-muted-foreground">
            and {ids.length - 3} more
          </li>
        )}
      </ul>
    );
  const hasAnyEvidence = result.conditions.some(
    (c) => (c.canonical_ids ?? []).length > 0,
  );

  return (
    <Table aria-label="Gate conditions">
      <TableHeader>
        <TableRow>
          <TableHead className="w-32">Status</TableHead>
          <TableHead>Condition</TableHead>
          <TableHead className="hidden w-28 md:table-cell">Severity</TableHead>
          {hasAnyEvidence && (
            <TableHead className="hidden md:table-cell">Evidence</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {result.conditions.map((c, i) => {
          const note = scopeNote(c.filter);
          const list = evidence(c.canonical_ids ?? []);
          return (
            <TableRow key={`${c.id}-${i}`}>
              <TableCell className="align-top whitespace-nowrap">
                <Outcome condition={c} />
              </TableCell>
              <TableCell className="align-top whitespace-normal">
                <div className="font-medium text-foreground">
                  {CONDITION_LABELS[c.id] ?? c.id}
                  {note && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      ({note})
                    </span>
                  )}
                </div>
                {c.summary &&
                  c.summary.trim().toLowerCase() !==
                    (CONDITION_LABELS[c.id] ?? c.id).trim().toLowerCase() && (
                    <div className="mt-0.5 text-sm text-muted-foreground">
                      {c.summary}
                    </div>
                  )}
                {!hasAnyEvidence && list && <div className="mt-2">{list}</div>}
              </TableCell>
              <TableCell className="hidden align-top text-xs text-muted-foreground md:table-cell">
                {c.level === "off"
                  ? "Off"
                  : c.level === "error"
                    ? "Blocking"
                    : "Advisory"}
              </TableCell>
              {hasAnyEvidence && (
                <TableCell className="hidden min-w-48 align-top md:table-cell">
                  {list ?? <span className="text-muted-foreground">–</span>}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
